/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import React from 'react';
import type { HeaderSize, Position, DraggableSize } from '@opengeoweb/shared';
import { ToolContainerDraggable, calculateStartSize } from '@opengeoweb/shared';
import type { uiTypes } from '@opengeoweb/store';
import { WarningEventSummaryForm } from './WarningEventSummaryForm';
import { useWarningsTranslation } from '../../../utils/i18n';

const DEFAULT_SUMMARY_SIZE = { width: 1200, height: 1050 };
const DEFAULT_SUMMARY_POSITION = { top: 80, right: 60 };
const DEFAULT_SUMMARY_MIN_SIZE = { width: 320, height: 300 };

export const WarningEventSummaryDialog: React.FC<{
  bounds?: string;
  title?: string;
  onClose: () => void;
  onMouseDown?: () => void;
  isOpen: boolean;
  isReadOnly: boolean;
  order?: number;
  source?: uiTypes.Source;
  size?: DraggableSize;
  startPosition?: Position;
  headerSize?: HeaderSize;
  onClickAdd?: () => void;
}> = ({
  bounds,
  onClose,
  title,
  isOpen,
  isReadOnly,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  size = DEFAULT_SUMMARY_SIZE,
  startPosition = DEFAULT_SUMMARY_POSITION,
  headerSize = 'xs',
  onClickAdd = (): void => {},
}) => {
  const { t } = useWarningsTranslation();
  const minSize = DEFAULT_SUMMARY_MIN_SIZE;
  const startSizeCalc = calculateStartSize(minSize, size, startPosition);
  const [sizeInState, setSizeInState] =
    React.useState<DraggableSize>(startSizeCalc);

  return (
    <ToolContainerDraggable
      bounds={bounds}
      headerSize={headerSize}
      isOpen={isOpen}
      minHeight={minSize.height}
      minWidth={minSize.width}
      onClose={onClose}
      onDragEnd={(_position, dragSize): void => {
        if (dragSize !== sizeInState) {
          setSizeInState(dragSize as DraggableSize);
        }
      }}
      onMouseDown={onMouseDown}
      onResizeStop={(_event, _direction, node): void => {
        const { offsetWidth: width, offsetHeight: height } = node;
        setSizeInState({ width, height });
      }}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={sizeInState}
      sx={{
        footer: {
          backgroundColor: 'inherit',
          boxShadow: 'none',
        },
      }}
      title={title || t('warning-event-summary-dialog-title')}
    >
      <Box sx={{ padding: 1, height: '100%' }}>
        <WarningEventSummaryForm
          isReadOnly={isReadOnly}
          onClickAdd={onClickAdd}
        />
      </Box>
    </ToolContainerDraggable>
  );
};
